import {
  Navbar as BPNavbar,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  Divider,
  Icon,
  Menu,
  MenuItem,
  NavbarGroup,
} from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import React, { useState } from "react";
import { NavLink } from "react-router-dom";

import { ACCOUNT_URL, productMap } from "../../constants";
import { useLogout } from "../../hooks";
import { fastDrawLogo } from "../../images";
import { SUPPORT_URL } from "../../redux/constants";
import { useAppSelector } from "../../redux/hooks";
import {
  UserPlaySettings,
  useGetUser,
  useGetUserPlaySettingsQuery,
  useUpdateUserPlaySettingsMutation,
} from "../../redux/slices";
import { FastModelProduct } from "../../types";
import { useSettingsReducer } from "../settings";
import { Settings } from "../settings/settings";
import styles from "./navbar.module.scss";

export interface NavbarListLink {
  link: string;
  label: string;
}

export const Navbar = () => {
  const { token } = useAppSelector((state) => state.auth);
  const { teamLogo: teamLogoUrl } = useAppSelector((state) => state.theme);
  const { user } = useGetUser();
  const { data: playSettings } = useGetUserPlaySettingsQuery();
  const [saveUserPlaySettigns] = useUpdateUserPlaySettingsMutation();

  const productNames: FastModelProduct[] = user?.products
    ? (Object.keys(user?.products) as FastModelProduct[]).sort((a) => (a === FastModelProduct.FAST_DRAW ? -1 : 0))
    : [];

  const products = productNames
    .filter((productName) => productMap.get(productName))
    .map((productName) => {
      const product = productMap.get(productName);

      return { productName, href: product?.link, logo: product?.logo };
    });

  const fastDrawProduct = user?.products?.FASTDRAW2?.[0];

  const isAdmin =
    user?.roles?.some((role) => ["Admin", "SuperAdmin"].includes(role)) ||
    fastDrawProduct?.roles?.includes("Account Admin") ||
    false;

  const listLinks: NavbarListLink[] = [{ label: "PLAYS", link: "home" }];

  const { logout } = useLogout(token);

  /** ======== Settings ======== */
  const [showSettings, setShowSettings] = useState(false);

  const openSettings = () => {
    setShowSettings(true);
  };

  const closeSettings = () => {
    setShowSettings(false);
  };

  const settingsReducer = useSettingsReducer({ initialSettings: playSettings });
  const { resetState } = settingsReducer;

  const cancelSettings = () => {
    resetState();
    closeSettings();
  };

  const saveSettings = (settings: UserPlaySettings) => {
    saveUserPlaySettigns(settings);
    closeSettings();
  };

  return (
    <BPNavbar className={classNames(styles.navbar, "bp4-dark")} fixedToTop>
      <NavbarGroup>
        <Popover2
          content={
            <Menu>
              {products.map((product, i) => (
                <MenuItem
                  key={i}
                  className={styles.productMenuItem}
                  {...(product.productName !== FastModelProduct.FAST_DRAW
                    ? { href: product.href, target: "_blank" }
                    : {})}
                  text={product.logo ? <img alt={product.productName} src={product.logo} /> : product.productName}
                  labelElement={product.productName === FastModelProduct.FAST_DRAW ? <Icon icon="tick" /> : null}
                />
              ))}
              {isAdmin && (
                <MenuItem className={styles.productMenuItem} href={ACCOUNT_URL} target="_blank" text="ADMIN PORTAL" />
              )}
            </Menu>
          }
          minimal
          placement="bottom-start"
        >
          <Button className={styles.popoverButton} minimal rightIcon="caret-down">
            <img alt="Fast Draw" src={fastDrawLogo} />
          </Button>
        </Popover2>
      </NavbarGroup>
      <NavbarGroup>
        {listLinks.map((link, i) => (
          <NavLink
            className={({ isActive }) => classNames(styles.navLink, { [styles.active || ""]: isActive })}
            to={`/${link.link}`}
            key={i}
          >
            {link.label}
          </NavLink>
        ))}
      </NavbarGroup>
      <NavbarGroup>
        <Popover2
          minimal
          placement="bottom-end"
          content={
            <Menu className={styles.accountMenu}>
              <MenuItem onClick={openSettings} icon="cog" text="Settings" />
              <MenuItem href={SUPPORT_URL} target="_blank" icon="help" text="Help" />
              <MenuItem icon="log-out" onClick={logout} text="Log Out" />
            </Menu>
          }
        >
          <Button minimal className={styles.accountButton} rightIcon="caret-down">
            {user?.userDetails?.displayName}
            <Divider />
            <img className={styles.teamLogo} src={teamLogoUrl} />
          </Button>
        </Popover2>
      </NavbarGroup>
      <Dialog title="Settings" isOpen={showSettings} onClose={closeSettings}>
        <DialogBody>{playSettings && <Settings settingsReducer={settingsReducer} />}</DialogBody>
        <DialogFooter
          actions={
            <>
              <Button onClick={cancelSettings} intent="none" minimal>
                Cancel
              </Button>
              <Button onClick={() => saveSettings(settingsReducer.settings)} intent="primary">
                Save
              </Button>
            </>
          }
        />
      </Dialog>
    </BPNavbar>
  );
};
